import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ieltsPath } from "./ieltsMocks";
import { FaBookReader, FaExclamationCircle } from "react-icons/fa";

function ReadingTestReady({ mock = 1 }) {
    const navigate = useNavigate();

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const handleStartTest = () => {
        navigate(ieltsPath(mock, "reading"));
    };

    const conditions = [
        "3 reading passages with 40 questions.",
        "60 minutes total test duration.",
        "Texts range from descriptive to analytical.",
        "No extra time for transferring answers.",
        "Questions include multiple choice, matching, and sentence completion.",
        "Test auto-submits when time expires.",
    ];

    const details = [
        { number: "60", label: "Minutes" },
        { number: "40", label: "Questions" },
        { number: "3", label: "Passages" },
        { number: "9.0", label: "Max Band" },
    ];

    return (
        <>
            <style>{`
                .reading-container {
                    min-height: 100vh;
                    background: #fafafa;
                    padding: 2rem;
                    padding-top: 96px;
                    display: flex;
                    align-items: flex-start;
                    justify-content: center;
                    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
                }

                .content-wrapper {
                    width: 100%;
                    max-width: 680px;
                    background: #ffffff;
                    border-radius: 20px;
                    padding: 3rem 3.25rem;
                    border: 1px solid #e8e8e8;
                    box-shadow: 0 4px 18px rgba(0, 0, 0, 0.07);
                }

                .icon-badge {
                    width: 68px;
                    height: 68px;
                    border-radius: 16px;
                    background: rgba(10, 137, 78, 0.08);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    margin: 0 auto 1.5rem auto;
                }

                .icon-badge svg {
                    font-size: 1.9rem;
                    color: #0a894e;
                }

                .main-title {
                    font-size: 2.1rem;
                    font-weight: 800;
                    margin: 0 0 0.6rem 0;
                    color: #0a894e;
                    text-align: center;
                    letter-spacing: -0.01em;
                }

                .subtitle {
                    font-size: 1.05rem;
                    color: #666;
                    text-align: center;
                    margin-bottom: 2.25rem;
                }

                .section-title {
                    font-size: 0.85rem;
                    font-weight: 700;
                    color: #0a894e;
                    text-transform: uppercase;
                    letter-spacing: 0.05em;
                    margin: 0 0 1rem 0;
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                }

                .section-title svg {
                    font-size: 1rem;
                }

                .conditions-box {
                    background: #f7f8f7;
                    border-radius: 14px;
                    padding: 0.25rem 1.5rem;
                    border: 1px solid #e5e5e5;
                    margin-bottom: 2rem;
                }

                .conditions-list {
                    list-style: none;
                    padding: 0;
                    margin: 0;
                }

                .condition-item {
                    color: #333;
                    padding: 1rem 0;
                    font-size: 1.02rem;
                    border-bottom: 1px solid #e8e8e8;
                    display: flex;
                    align-items: flex-start;
                    line-height: 1.45;
                }

                .condition-item:last-child {
                    border-bottom: none;
                }

                .condition-item::before {
                    content: "";
                    min-width: 7px;
                    height: 7px;
                    border-radius: 50%;
                    background: #0a894e;
                    margin-right: 0.9rem;
                    margin-top: 0.55rem;
                }

                .test-details {
                    display: grid;
                    grid-template-columns: repeat(4, 1fr);
                    gap: 0.85rem;
                    margin-bottom: 2.25rem;
                }

                .detail-card {
                    background: #f7f8f7;
                    border-radius: 12px;
                    padding: 1rem 0.5rem;
                    border: 1px solid #e5e5e5;
                    text-align: center;
                    transition: all 0.2s ease;
                }

                .detail-card:hover {
                    transform: translateY(-2px);
                    border-color: #0a894e;
                }

                .detail-number {
                    font-size: 1.5rem;
                    font-weight: 800;
                    color: #0a894e;
                    margin-bottom: 0.15rem;
                }

                .detail-label {
                    color: #777;
                    font-size: 0.78rem;
                    font-weight: 600;
                }

                .button-group {
                    display: flex;
                    gap: 1rem;
                    justify-content: center;
                }

                .start-button {
                    flex: 1;
                    background: #0a894e;
                    color: #fff;
                    border: none;
                    padding: 1.05rem 1.75rem;
                    font-size: 1.05rem;
                    font-weight: 600;
                    border-radius: 12px;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 0.7rem;
                    transition: all 0.2s ease;
                }

                .start-button:hover {
                    background: #087a44;
                    transform: translateY(-2px);
                    box-shadow: 0 8px 18px rgba(10, 137, 78, 0.28);
                }

                .exit-button {
                    flex: 1;
                    background: transparent;
                    color: #444;
                    border: 1px solid #d8d8d8;
                    padding: 1.05rem 1.75rem;
                    font-size: 1.05rem;
                    font-weight: 600;
                    border-radius: 12px;
                    cursor: pointer;
                    transition: all 0.2s ease;
                }

                .exit-button:hover {
                    background: #f5f5f5;
                    border-color: #bbb;
                    transform: translateY(-2px);
                }

                .book-icon {
                    font-size: 0.95rem;
                }

                /* Responsive Design */
                @media (max-width: 720px) {
                    .content-wrapper {
                        max-width: 100%;
                        padding: 2.25rem 1.75rem;
                    }
                }

                @media (max-width: 560px) {
                    .reading-container {
                        padding: 1rem;
                        padding-top: 76px;
                    }

                    .content-wrapper {
                        padding: 1.75rem 1.5rem;
                        border-radius: 16px;
                    }

                    .main-title {
                        font-size: 1.6rem;
                    }

                    .subtitle {
                        font-size: 0.95rem;
                    }

                    .condition-item {
                        font-size: 0.92rem;
                    }

                    .test-details {
                        grid-template-columns: repeat(2, 1fr);
                    }

                    .button-group {
                        flex-direction: column;
                    }
                }
            `}</style>

            <div className="reading-container">
                <div className="content-wrapper">
                    <div className="icon-badge">
                        <FaBookReader />
                    </div>

                    <h1 className="main-title">Reading Test Ready</h1>
                    <p className="subtitle">
                        Prepare for your IELTS {mock === 1 ? "" : `Mock ${mock} `}Reading assessment.
                    </p>

                    <h2 className="section-title">
                        <FaExclamationCircle />
                        Exam Conditions Active
                    </h2>

                    <div className="conditions-box">
                        <ul className="conditions-list">
                            {conditions.map((condition, index) => (
                                <li className="condition-item" key={index}>
                                    {condition}
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className="test-details">
                        {details.map((detail, index) => (
                            <div className="detail-card" key={index}>
                                <div className="detail-number">
                                    {detail.number}
                                </div>
                                <div className="detail-label">
                                    {detail.label}
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="button-group">
                        <button
                            className="start-button"
                            onClick={handleStartTest}
                        >
                            <FaBookReader className="book-icon" />
                            <span>Start Test</span>
                        </button>

                        <button
                            className="exit-button"
                            onClick={() => navigate("/")}
                        >
                            Exit Module
                        </button>
                    </div>
                </div>
            </div>
        </>
    );
}

export default ReadingTestReady;